import { FORBIDDEN_PAGE } from 'common/messages'
import { Rules } from 'common/rules'
import { AlertNotification } from 'components/AlertNotification/AlertNotification'
import { Loader } from 'components/Loader'
import ScrollToTop from 'components/Share/ScrollToTop'
import { WelcomeModal } from 'components/WelcomeModal/WelcomeModal'
import { LayoutContextProvider } from 'contexts/LayoutContext'
import { SliderContextProvider } from 'contexts/SliderContext'
import { PortalThemeProvider } from 'core/theme/ThemeProvider'
import { useFetchUserClaim } from 'hooks/useFetchUserClaim'
import useNotification from 'hooks/useNotification'
import { DefaultErrorBoundary } from 'modules/DefaultErrorBoundary'
import { ContactUs } from 'pages/ContactUs'
import { Dashboard } from 'pages/Dashboard'
import { ErrorPage } from 'pages/ErrorPage'
import { Grounding } from 'pages/Grounding'
import { SearchResultsPage } from 'pages/SearchResultsPage'
import { Fragment, lazy, PropsWithChildren, Suspense, useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import { IntlProvider } from 'react-intl'
import { BrowserRouter, Redirect, Route, RouteProps, Switch } from 'react-router-dom'
import { useGlobalStore } from 'store/useGlobalStore'
import { DtmPageName, setPageInfo } from 'utils/analyticsUtils'
import shallow from 'zustand/shallow'

interface IAuthenticatedRouteProps extends PropsWithChildren<RouteProps> {
  allowedRules?: Rules[]
  pageName?: string
}

interface IAnalyticsRouteProps extends RouteProps {
  pageName?: string
}

const AnalyticsRoute = ({ pageName, ...props }: IAnalyticsRouteProps) => {
  useMemo(() => {
    if (pageName) {
      setPageInfo(pageName)
    }
  }, [pageName])

  return <Route {...props} />
}

const AuthenticatedRoute = ({ pageName, allowedRules, children, ...props }: IAuthenticatedRouteProps) => {
  const [rules, isInRule] = useGlobalStore((state) => [state.rules, state.isInRule], shallow)
  const [allow, setAllow] = useState<boolean>(false)

  useNotification()

  useEffect(() => {
    allowedRules?.forEach((rule) => {
      if (isInRule(rule)) {
        setAllow(true)
        return
      }
    })
  }, [rules, allowedRules, isInRule])

  return (
    <AnalyticsRoute pageName={pageName} {...props}>
      {!allow ? (
        <ErrorPage message={FORBIDDEN_PAGE} />
      ) : (
        <Suspense fallback={<Loader />}>
          <DefaultErrorBoundary>{children}</DefaultErrorBoundary>
        </Suspense>
      )}
    </AnalyticsRoute>
  )
}

const PublicRoute = ({ children, ...props }: RouteProps) => {
  return (
    <BrowserRouter forceRefresh>
      <Route path={props.path}>
        <Suspense fallback={<Loader />}>
          <DefaultErrorBoundary>{children}</DefaultErrorBoundary>
        </Suspense>
      </Route>
    </BrowserRouter>
  )
}

export const App = () => {
  const [localText, isAuthenticated, showDelayedWelcomeModal, dismissWelcomeMessage, initializeDelayedWelcomeModal] =
    useGlobalStore((state) => [
      state.localText,
      state.isAuthenticated,
      state.showDelayedWelcomeModal,
      state.dismissWelcomeMessage,
      state.initializeDelayedWelcomeModal
    ])
  const { loading: userClaimLoading } = useFetchUserClaim()

  useEffect(() => {
    initializeDelayedWelcomeModal(isAuthenticated, userClaimLoading)
  }, [isAuthenticated, userClaimLoading, initializeDelayedWelcomeModal])

  if (userClaimLoading) {
    return <Loader overlay />
  }
  const Faq = lazy(() => import('./pages/Faq'))
  const VehicleDetailsPage = lazy(() => import('./pages/VehicleDetailsPage'))
  return (
    <div>
      {localText && (
        <IntlProvider textComponent={Fragment} locale={'en-GB'} messages={localText}>
          <PortalThemeProvider>
            <Helmet titleTemplate="%s | Nissan Dealer Portal"></Helmet>
            <LayoutContextProvider>
              <AlertNotification />
              <BrowserRouter>
                <ScrollToTop />
                <Switch>
                  <PublicRoute path="/faqs">
                    <Faq />
                  </PublicRoute>
                  <AnalyticsRoute pageName={DtmPageName.ContactUs} path="/contactUs">
                    <ContactUs />
                  </AnalyticsRoute>
                  {!userClaimLoading && isAuthenticated ? (
                    <>
                      {showDelayedWelcomeModal && <WelcomeModal onHide={dismissWelcomeMessage} />}
                      <SliderContextProvider>
                        <Switch>
                          <AuthenticatedRoute
                            pageName={DtmPageName.VDP}
                            path={['/vehicle/:vehicleInstanceId']}
                            allowedRules={[Rules.PAGE_RDP_Dashboard]}
                          >
                            <VehicleDetailsPage></VehicleDetailsPage>
                          </AuthenticatedRoute>
                          <AuthenticatedRoute
                            pageName={DtmPageName.Dashboard}
                            path={['/dashboard', '/']}
                            allowedRules={[Rules.PAGE_RDP_Dashboard]}
                            exact
                          >
                            <Dashboard />
                          </AuthenticatedRoute>
                          <AuthenticatedRoute
                            pageName={DtmPageName.SRP}
                            path={['/search']}
                            allowedRules={[Rules.PAGE_RDP_SRP]}
                            exact
                          >
                            <SearchResultsPage />
                          </AuthenticatedRoute>
                          <AuthenticatedRoute
                            pageName={DtmPageName.Grounding}
                            path={['/grounding']}
                            allowedRules={[Rules.VIEW_GROUNDING_QUICK_START, Rules.VIEW_GROUNDING_ACTIVITY]} //review the rules needed
                            exact
                          >
                            <Grounding />
                          </AuthenticatedRoute>
                          <Redirect to="/" />
                        </Switch>
                      </SliderContextProvider>
                    </>
                  ) : (
                    <Redirect
                      to={{
                        pathname: '/login',
                        search: `?returnUrl=${window.location.pathname}${window.location.search}`
                      }}
                    />
                  )}
                </Switch>
              </BrowserRouter>
            </LayoutContextProvider>
          </PortalThemeProvider>
        </IntlProvider>
      )}
    </div>
  )
}
